import type { AxiosError } from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api, getIsOnline } from '../../api';
import {
  getOfflineGuestInfo,
  getSessionGuestPreferences,
  saveOfflineGuestInfo,
  saveSessionGuestPreferences,
} from '../../api/guest';
import { isNative, type Org } from '../../helpers/device.helper';
import type { LabelGroup } from '../../models/advocate';
import type { ErrorMessage } from '../../models/Error';
import type { MoreInfoPages } from '../../models/guest';
import type { Language } from '../../models/language';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';
import {
  checkHasNewFirstAlertsCommunity,
  getAlertAcceptedDisclaimer,
  getAlertPromptResponse,
  getDartAlertInfo,
} from './firstAlertsSlice';

const { logError } = RaygunErrorHandlerService();

export type GuestInfo = {
  guest_id: string;
  org_code: Org;
  use_dart: boolean;
  use_service_providers_coming_soon: boolean;
  use_service_providers: boolean;
  use_first_alerts: boolean;
  use_fa_coming_soon: boolean;
  access_shared_peers: boolean;
};

export type OrgDetails = {
  code: Org;
  name: string;
};

export const WIZARD_MODAL = {
  IDLE: 'idle',
  SAFE_EXIT: 'safe_exit',
  SORT: 'sort',
} as const;

export type WIZARD_MODAL = (typeof WIZARD_MODAL)[keyof typeof WIZARD_MODAL];

type GuestSliceType = {
  guestInfo: GuestInfo | undefined;
  languageList: Language[];
  preferenceOptions: LabelGroup[];
  guestLabelIds: string[];
  guestIsBlocked: boolean | undefined;
  areas: OrgDetails[];
  guestArea: OrgDetails | undefined;
  showMoreInfo: Record<MoreInfoPages, boolean>;
  wizardModal: WIZARD_MODAL;
};

const initialState: GuestSliceType = {
  guestInfo: undefined,
  languageList: [],
  preferenceOptions: [],
  guestLabelIds: [],
  guestIsBlocked: undefined,
  areas: [],
  guestArea: undefined,
  showMoreInfo: {
    leps: false,
    services: false,
    area_select: false,
    language_select: false,
    first_alerts: false,
  },
  wizardModal: WIZARD_MODAL.IDLE,
};

export const fetchGuestInfo = createAsyncThunk<GuestInfo | undefined, undefined, AsyncThunkConfig>(
  'appStatus/fetchGuestInfo',
  async (_, thunkAPI) => {
    try {
      const online = await getIsOnline();
      if (!online && isNative()) {
        const guestInfo = await getOfflineGuestInfo();
        if (guestInfo) {
          return guestInfo;
        }
        return;
      }

      const response = await api.post<GuestInfo | undefined>('/guest/v1_get_guest_info');
      if (response && isNative()) {
        saveOfflineGuestInfo(response);
      }
      return response;
    } catch (e) {
      logError(e, ['appStatusSlice', 'fetchGuestInfo']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getLanguageList = createAsyncThunk<Language[], undefined, AsyncThunkConfig>(
  'guest/getLanguageList',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<Language[]>('/guest/v1_get_languages');
      return response ?? [];
    } catch (e) {
      logError(e, ['guestSlice', 'getLanguageList']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getAreas = createAsyncThunk<OrgDetails[], undefined, AsyncThunkConfig>(
  'guest/getAreas',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<OrgDetails[]>('/guest/v0_get_areas');
      return response ?? [];
    } catch (e) {
      logError(e, ['guestSlice', 'getAreas']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type SaveAreaParams = { new_org_code: Org; selected_language_code?: string };
type SaveAreResponse = { saved: boolean; is_valid_language: boolean };
export const saveArea = createAsyncThunk<SaveAreResponse, SaveAreaParams, AsyncThunkConfig>(
  'guest/saveArea',
  async (params, thunkAPI) => {
    try {
      const response = await api.post<SaveAreResponse>('/guest/v0_save_area', params);
      if (response?.saved) {
        await thunkAPI.dispatch(getLanguageList());
        await thunkAPI.dispatch(fetchGuestInfo());
      }
      return response ?? { saved: false, is_valid_language: false };
    } catch (e) {
      logError(e, ['guestSlice', 'saveArea']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getPreferenceOptions = createAsyncThunk<LabelGroup[], undefined, AsyncThunkConfig>(
  'guest/getPreferenceOptions',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<LabelGroup[]>('/guest/v0_get_preference_options');
      return response ?? [];
    } catch (e) {
      logError(e, ['guestSlice', 'getPreferenceOptions']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getPreferences = createAsyncThunk<string[], undefined, AsyncThunkConfig>(
  'guest/getPreferences',
  async (_, thunkAPI) => {
    try {
      if (isNative()) {
        const response = await api.post<string[]>('/guest/v1_get_preferences');
        return response ?? [];
      } else {
        return getSessionGuestPreferences();
      }
    } catch (e) {
      logError(e, ['guestSlice', 'getPreferenceOptions']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const savePreferences = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'guest/savePreferences',
  async (_, thunkAPI) => {
    try {
      const label_ids = thunkAPI.getState().guestSlice.guestLabelIds;
      const response = await api.post<boolean>('/guest/v0_save_preferences', { label_ids });
      if (!isNative()) {
        saveSessionGuestPreferences(label_ids);
      }
      if (response) {
        thunkAPI.dispatch(getPreferences());
      }
      return response ?? false;
    } catch (e) {
      logError(e, ['guestSlice', 'savePreferences']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const resetPreferences = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'guest/resetPreferences',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_reset_preferences');
      return response ?? false;
    } catch (e) {
      logError(e, ['guestSlice', 'resetPreferences']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getGuestBlockedStatus = createAsyncThunk<boolean | undefined, undefined, AsyncThunkConfig>(
  'guest/getGuestBlockedStatus',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_get_blocked_status');
      return response;
    } catch (e) {
      logError(e, ['guestSlice', 'getGuestBlockedStatus']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

// oxlint-disable-next-line typescript/no-invalid-void-type
export const loadGuestData = createAsyncThunk<void, undefined, AsyncThunkConfig>(
  'firstAlerts/loadGuestData',
  async (_, thunkAPI) => {
    thunkAPI.dispatch(getPreferences());

    if (isNative()) {
      thunkAPI.dispatch(getAlertPromptResponse());
      thunkAPI.dispatch(checkHasNewFirstAlertsCommunity());
      thunkAPI.dispatch(getAlertAcceptedDisclaimer());
      thunkAPI.dispatch(getDartAlertInfo());
    }
  },
);

export const guestSlice = createSlice({
  name: 'guest',
  initialState,
  reducers: {
    setGuestBlockedStatus: (state, action: PayloadAction<boolean>) => {
      state.guestIsBlocked = action.payload;
    },
    setGuestLabelIds: (state, action: PayloadAction<string[]>) => {
      state.guestLabelIds = action.payload;
    },
    setShowMoreInfo: (state, action: PayloadAction<MoreInfoPages>) => {
      state.showMoreInfo[action.payload] = !state.showMoreInfo[action.payload];
    },
    setWizardModal: (state, action: PayloadAction<WIZARD_MODAL>) => {
      state.wizardModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestInfo.fulfilled, (state, action) => {
        state.guestInfo = action.payload;
        state.guestArea = state.areas.find((a) => a.code === action.payload?.org_code);
      })
      .addCase(getLanguageList.fulfilled, (state, action) => {
        state.languageList = action.payload;
      })
      .addCase(getAreas.fulfilled, (state, action) => {
        state.areas = action.payload;
      })
      .addCase(getPreferences.fulfilled, (state, action) => {
        state.guestLabelIds = action.payload;
      })
      .addCase(getPreferenceOptions.fulfilled, (state, action) => {
        state.preferenceOptions = action.payload.filter((l) => l.group_alias !== 'livedExperiences');
      })
      .addCase(getGuestBlockedStatus.fulfilled, (state, action) => {
        state.guestIsBlocked = action.payload;
      });
  },
});

export const { setGuestBlockedStatus, setGuestLabelIds, setShowMoreInfo, setWizardModal } = guestSlice.actions;
